// @ts-nocheck
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/styles";
import _ from "lodash";
import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";
import DateRangePicker from "src/components/TimePicker/DateRangePicker";
import { getDevices } from "src/repos/devices";
import { getDeviceMetrics } from "src/repos/devices/views";
import { getTimeseries } from "src/repos/timeseries";
import palette from "src/theme/palette";
import { calcGranularity } from "src/utils/analytics";
import { download } from "src/utils/download";
import { segmentsMappedWithTelemetry } from "src/utils/machineTelemetryMapping";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme: any) => ({
  root: {},
  button: { marginTop: theme.spacing(3), marginBottom: theme.spacing(2) },
  checkboxWrapper: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "stretch",
  },
  checkbox: {
    width: "250px",
  },
  flexCol: {
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    paddingBottom: 0,
    paddingTop: 0,
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  input: {
    minWidth: "300px",
    width: "50%",
  },
  picker: { marginTop: theme.spacing(2) },
  link: {
    color: palette.text.link,
    fontSize: "14px",
    textTransform: "none",
    fontWeight: 400,
    cursor: "pointer",
  },
  buttonContainer: {
    flexDirection: "row",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  captionContainer: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 20,
  },
  timer: {
    color: palette.secondary.dark,
  },
}));

function Measures() {
  const classes = useStyles();
  const initialDatePast = moment().subtract(20, "minutes");
  const [selectedMachineId, setSelectedMachineId] = useState<string>("");
  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState(initialDatePast);
  const [selectedEndDate, setSelectedEndDate] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState([] as string[]);
  const [selectedAdditionalMetrics, setSelectedAdditionalMetrics] = useState(
    []
  );
  const [additionalMetrics, setAdditionalMetrics] = useState([]);
  const [machineList, setMachineList] = useState<any>([]);
  const metricMultiSelect = segmentsMappedWithTelemetry("");
  const [reportData, setReportData] = useState<any>();
  const { t } = useTranslation(["glossary", "common"]);

  const basicTimeRanges = [
    { key: "24-hours", label: t("last_24_hours") },
    { key: "7-days", label: t("last_7_days") },
    { key: "30-days", label: t("last_30_days") },
  ];
  const extraTimeRanges = [
    { key: "3-months", label: t("last_3_months") },
    { key: "1-Year", label: t("last_year") },
    { key: "custom", label: t("custom_range") },
  ];

  useEffect(() => {
    getDevices()
      .then((r) => r.json())
      .then(({ data }) => setMachineList(data));
  }, []);

  useEffect(() => {
    setSelectedMetrics([]);
    setSelectedTimeframe("");

    if (!_.isNil(selectedMachineId)) {
      const selectedDevice = _.find(machineList, ["id", selectedMachineId]);
      if (selectedDevice) {
        getDeviceMetrics(selectedDevice.id).then((data) => {
          setAdditionalMetrics(data);
        });
      }
    }
  }, [selectedMachineId, machineList]);

  useEffect(() => {
    if (_.isNil(selectedTimeframe) || selectedTimeframe === "custom") return;

    const [amount, unit] = selectedTimeframe.split("-");

    setSelectedStartDate(moment().add(-1 * Number(amount), unit as any));
  }, [selectedTimeframe]);

  const handleMetricsChange = (e: any) => {
    if (e.target.checked) {
      setSelectedMetrics([...selectedMetrics, e.target.value] as []);
    }
    if (!e.target.checked) {
      setSelectedMetrics(
        selectedMetrics.filter(
          (selectedMetrics) => selectedMetrics !== e.target.value
        )
      );
    }
  };

  const handleRangeChange = (startDate: Moment, endDate: Moment) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
  };

  const onSubmitHandler = (e: any) => {
    e.preventDefault();
    if (!loading) {
      setLoading(true);
      getTimeseries({
        metrics: [...selectedMetrics, ...selectedAdditionalMetrics],
        start_time: selectedStartDate.toISOString(),
        end_time: selectedEndDate.toISOString(),
        device_id: selectedMachineId,
        format: "csv",
        granularity: calcGranularity(
          selectedStartDate.toISOString(),
          selectedEndDate.toISOString()
        ),
      })
        .then((r) => r.text())
        .then((csv) => {
          getDevices()
            .then((r) => r.json())
            .then(({ data }) => {
              setLoading(false);
              setMachineList(data);
              setReportData(csv);
              if (csv !== "") download("helios-report.csv", csv);
            });
        });
    }
  };

  const getTotalSelectedMetrics = () => {
    return selectedMetrics.length + selectedAdditionalMetrics.length;
  };

  const isFormValid = () => {
    return (
      !_.isEmpty(selectedMachineId) &&
      !_.isEmpty(selectedTimeframe) &&
      getTotalSelectedMetrics() > 0
    );
  };

  // No tag limit
  const isMetricMax = () => false;

  return (
    <Card>
      <CardContent>
        <form onSubmit={onSubmitHandler}>
          <div className={classes.flexCol}>
            <FormControl required className={classes.formControl}>
              <Typography gutterBottom variant="h5">
                {t("which_machine_message")}
              </Typography>
              <Select
                variant="outlined"
                color="primary"
                className={classes.input}
                value={selectedMachineId}
                onChange={(e: React.FormEvent<EventTarget>) =>
                  setSelectedMachineId((e.target as HTMLInputElement).value)
                }
              >
                {machineList.map((machine) => (
                  <MenuItem value={machine.id}>
                    {machine.name ? machine.name : machine.serial_number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl required className={classes.formControl}>
              <Typography gutterBottom variant="h5">
                {t("choose_the_timeframe")}
              </Typography>

              <Select
                variant="outlined"
                color="primary"
                value={selectedTimeframe}
                onChange={(e: React.FormEvent<EventTarget>) =>
                  setSelectedTimeframe((e.target as HTMLInputElement).value)
                }
                className={classes.input}
              >
                {basicTimeRanges.map(({ key, label }) => (
                  <MenuItem value={key}>{label}</MenuItem>
                ))}
                {extraTimeRanges.map(({ key, label }) => (
                  <MenuItem value={key}>{label}</MenuItem>
                ))}
              </Select>

              {selectedTimeframe === "custom" && (
                <div className={classes.picker}>
                  <DateRangePicker
                    onApply={(
                      _event: any,
                      picker: { startDate: Moment; endDate: Moment }
                    ) => handleRangeChange(picker.startDate, picker.endDate)}
                    open="left"
                    drops="auto"
                    startDate={selectedStartDate}
                    endDate={selectedEndDate}
                    inputStartDate={selectedStartDate}
                    inputEndDate={selectedEndDate}
                    maxDate={new Date()}
                  ></DateRangePicker>
                </div>
              )}
            </FormControl>

            <FormControl
              required
              component="fieldset"
              className={classes.formControl}
            >
              <Typography gutterBottom variant="h5">
                {t("what_report_contents_message")}
              </Typography>

              <FormGroup className={classes.checkboxWrapper}>
                {metricMultiSelect.map(
                  ({ tag, label }: { tag: string; label: string }) => (
                    <FormControlLabel
                      className={classes.checkbox}
                      control={
                        <Checkbox
                          color="primary"
                          onChange={(e) => handleMetricsChange(e)}
                          name={label}
                          value={tag}
                          checked={selectedMetrics.includes(tag)}
                          disabled={
                            isMetricMax() && !selectedMetrics.includes(tag)
                          }
                        />
                      }
                      label={label}
                    />
                  )
                )}
              </FormGroup>
            </FormControl>

            <FormControl required className={classes.formControl}>
              <Typography gutterBottom variant="h6">
                {t("additional_metrics")}
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                id="checkboxes-additional-metrics"
                className={classes.input}
                options={additionalMetrics}
                onChange={(_event, value) =>
                  setSelectedAdditionalMetrics(value)
                }
                getOptionDisabled={(_option) => isMetricMax()}
                noOptionsText={t("no_options")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t("select_metrics")}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      color="primary"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </FormControl>

            <div className={classes.buttonContainer}>
              <Button
                type="submit"
                color={loading ? "primary" : "secondary"}
                variant="contained"
                className={classes.button}
                disabled={!isFormValid()}
              >
                {loading ? t("generating") + "..." : t("generate_report")}
              </Button>
            </div>
            {reportData === "" && (
              <Typography>{t("data_not_found")}</Typography>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default Measures;